<template>
  <div
    :class="[
      `x-tool-tip x-tool-tip--${direction} x-tool-tip--border-radius-${borderRadius} ${this.class}`,
      {
        'x-tool-tip--hidden': !visible,
        'x-tool-tip--fixed-position': positionFixed,
      },
    ]"
  >
    <div class="x-tool-tip__wrapper">
      <div class="x-tool-tip__content" data-test="tool-tip-label">
        <slot />
      </div>
    </div>
  </div>
</template>

<!-- eslint-disable vue/block-lang -->
<script>
/* eslint-disable vue/component-api-style */
import pubsub from '@/lib/pubsub'

/**
 * @deprecated Use `<ys-tooltip>` from the latest `@onxmaps/yellowstone-ui` release instead.
 */
export default {
  name: 'XToolTip',
  props: {
    trigger: {
      type: String,
      default: 'hover',
      validator: (value) => ['hover', 'always', 'event'].includes(value),
    },
    direction: {
      type: String,
      default: 'left',
      validator: (value) =>
        ['top', 'right', 'bottom', 'left', 'right-top', 'left-top'].includes(
          value
        ),
    },
    // eslint-disable-next-line vue/no-unused-properties
    class: {
      type: [String, Array],
      default: '',
    },
    event: {
      type: String,
      default: '',
    },
    arrowSize: {
      type: Number,
      default: 12,
    },
    offset: {
      type: Number,
      default: 0,
    },
    borderRadius: {
      type: String,
      default: 'sm',
      validator: (value) => ['sm', 'md', 'lg', 'xl'].includes(value),
    },
    positionFixed: {
      type: Boolean,
      default: false,
    },
    dynamicTop: {
      type: Number,
      default: 0,
    },
    dynamicLeft: {
      type: Number,
      default: 0,
    },
    delay: {
      type: Number,
      default: 500,
    },
  },
  emits: ['close', 'tooltipViewed'],
  data() {
    return {
      visible: false,
      buttonTop: 0,
      buttonLeft: 0,
    }
  },
  computed: {
    arrowSizePx() {
      return this.arrowSize + 'px'
    },
    offsetPx() {
      return this.offset + 'px'
    },
    dynamicTopPx() {
      return this.buttonTop + this.dynamicTop + 'px'
    },
    dynamicLeftPx() {
      return this.buttonLeft + this.dynamicLeft + 'px'
    },
  },
  mounted() {
    switch (this.trigger) {
      case 'always':
        this.visible = true
        break
      case 'event':
        this.initEvent()
        break
      case 'hover':
        this.initHover()
        break
      default:
        this.initHover()
    }
  },
  methods: {
    initHover() {
      let timeout

      this.$el.parentElement.addEventListener('mouseenter', () => {
        timeout = setTimeout(() => {
          if (this.positionFixed) {
            this.getElementBounds(this.$el.parentElement)
          }
          this.visible = true
          this.$emit('tooltipViewed')
        }, this.delay)
      })

      this.$el.parentElement.addEventListener('mouseleave', () => {
        clearTimeout(timeout)
        this.visible = false
      })
    },
    initEvent() {
      pubsub.subscribe(this.event, () => {
        this.visible = true
      })
    },
    getElementBounds(el) {
      const rect = el?.getBoundingClientRect()

      if (rect) {
        this.buttonTop = rect.y
        this.buttonLeft = rect.right
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@use '@/styles/system/_variables.scss';
@use '@/styles/system/_colors.scss';
@use '@/styles/system/_shadow.scss';
@use '@/styles/system/_fonts.scss';
@use '@/styles/_layer-map.scss';

// general styles
.x-tool-tip {
  display: table; // creates actual height of tooltip versus 0 -> needed for calculatePosition()
  position: absolute;
  z-index: layer-map.$z-index-level-tooltips;
  pointer-events: none;
  transition: opacity 0.5s ease;

  &--hidden {
    opacity: 0;
  }

  &__wrapper {
    background: colors.$BaseWhite;
    padding: 3px 8px;
    box-shadow: shadow.$Low;

    &:after {
      content: '';
      position: absolute;
      z-index: 0;
      width: v-bind(arrowSizePx);
      height: v-bind(arrowSizePx);
      background: colors.$BaseWhite;
      transform: rotate(45deg);
      border-radius: 3px;
    }

    .x-tool-tip--border-radius-sm & {
      border-radius: variables.$border-radius-sm;
    }

    .x-tool-tip--border-radius-md & {
      border-radius: variables.$border-radius-md;
    }

    .x-tool-tip--border-radius-lg & {
      border-radius: variables.$border-radius-lg;
    }

    .x-tool-tip--border-radius-xl & {
      border-radius: variables.$border-radius-xl;
    }
  }

  &__content {
    position: relative;
    z-index: 1;
    white-space: nowrap;
    font-size: 14px;
    line-height: 1.6;
    font-family: fonts.$onxPrimaryFont;
    text-align: center;
  }

  &--top {
    bottom: 100%;
    left: 50%;
    transform: translate(-50%, 0);
    margin-bottom: calc(v-bind(arrowSizePx) / 2 + v-bind(offsetPx));

    .x-tool-tip__wrapper {
      &:after {
        bottom: calc(v-bind(arrowSizePx) / 2 * -1 + 1.5px);
        left: calc(50% - v-bind(arrowSizePx) / 2);
        // adapted from shadow.$Low
        box-shadow: 0.2rem 0.2rem 0.3125rem 0 rgba(0, 0, 0, 0.14);
      }
    }
  }

  &--right {
    top: 50%;
    transform: translate(0, -50%);
    left: 100%;
    margin-left: calc(v-bind(arrowSizePx) / 2 + v-bind(offsetPx));

    .x-tool-tip__wrapper {
      &::after {
        top: calc(50% - v-bind(arrowSizePx) / 2);
        left: calc(v-bind(arrowSizePx) / 2 * -1 + 1.5px);
        // adapted from shadow.$Low
        box-shadow: -0.2rem 0.2rem 0.3125rem 0 rgba(0, 0, 0, 0.14);
      }
    }
  }

  &--right-top {
    top: calc(v-bind(arrowSizePx) * -1);
    left: 100%;
    margin-left: calc(v-bind(arrowSizePx) / 2 + v-bind(offsetPx));

    .x-tool-tip__wrapper {
      &::after {
        top: calc(v-bind(arrowSizePx) + v-bind(arrowSizePx) / 2);
        left: calc(v-bind(arrowSizePx) / 2 * -1 + 1.5px);
        // adapted from shadow.$Low
        box-shadow: -0.2rem 0.2rem 0.3125rem 0 rgba(0, 0, 0, 0.14);
      }
    }
  }

  &--bottom {
    top: 100%;
    left: 50%;
    transform: translate(-50%, 0);
    margin-top: calc(v-bind(arrowSizePx) / 2 + v-bind(offsetPx));

    .x-tool-tip__wrapper {
      &::after {
        top: calc(v-bind(arrowSizePx) / 2 * -1 + 1.5px);
        left: calc(50% - v-bind(arrowSizePx) / 2);
        // adapted from shadow.$Low
        box-shadow: -0.2rem -0.2rem 0.3125rem 0 rgba(0, 0, 0, 0.14);
      }
    }
  }

  &--left {
    top: 50%;
    transform: translate(0, -50%);
    right: 100%;
    margin-right: calc(v-bind(arrowSizePx) / 2 + v-bind(offsetPx));

    .x-tool-tip__wrapper {
      &::after {
        top: calc(50% - v-bind(arrowSizePx) / 2);
        right: calc(v-bind(arrowSizePx) / 2 * -1 + 1.5px);
        // adapted from shadow.$Low
        box-shadow: 0.2rem -0.2rem 0.3125rem 0 rgba(0, 0, 0, 0.14);
      }
    }
  }

  &--left-top {
    top: calc(v-bind(arrowSizePx) * -1);
    right: 100%;
    margin-right: calc(v-bind(arrowSizePx) / 2 + v-bind(offsetPx));

    .x-tool-tip__wrapper {
      &::after {
        top: calc(v-bind(arrowSizePx) + v-bind(arrowSizePx) / 2);
        right: calc(v-bind(arrowSizePx) / 2 * -1 + 1.5px);
        // adapted from shadow.$Low
        box-shadow: 0.2rem -0.2rem 0.3125rem 0 rgba(0, 0, 0, 0.14);
      }
    }
  }

  &--fixed-position {
    position: fixed;
    top: v-bind(dynamicTopPx);
    left: v-bind(dynamicLeftPx);
  }
}
</style>
